import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Settings as SettingsIcon, Eye, EyeOff, Mail, Gift, Copy, Accessibility, Link2, Unlink } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { getTextSize, setTextSize, getReduceMotion, setReduceMotion } from "../lib/theme";

const TEXT_SIZE_OPTIONS = [
  { value: "default", label: "Default", sample: "text-sm" },
  { value: "large", label: "Large", sample: "text-base" },
  { value: "xl", label: "Extra large", sample: "text-lg" },
];

const EMAIL_PREFS = [
  { key: "email_rank_alerts", label: "Leaderboard rivalry alerts", desc: "Get an email when another collector overtakes you (or you overtake them) on the leaderboard." },
  { key: "email_chase_alerts", label: "Chase Deck matches", desc: "Get an email when a card from your Chase Deck appears in another collector's public binder." },
  { key: "email_weekly_digest", label: "Weekly collection digest", desc: "A weekly email with your collection value, how it changed, and your top price mover." },
];

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [binderName, setBinderName] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [showValue, setShowValue] = useState(true);
  const [emailPrefs, setEmailPrefs] = useState({});
  const [referral, setReferral] = useState(null);
  const [textSize, setTextSizeState] = useState(getTextSize());
  const [reduceMotion, setReduceMotionState] = useState(getReduceMotion());
  const [saving, setSaving] = useState(false);
  const [ebayConn, setEbayConn] = useState(null);
  const [ebayBusy, setEbayBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("ebay");
    if (!status) return;
    if (status === "connected") toast.success("Your eBay seller account is connected");
    else if (status === "declined") toast.error("eBay connection cancelled");
    else if (status === "expired") toast.error("The connect link expired — please try again");
    else if (status === "test_button") toast.info("That was eBay's built-in Test Sign-In — it skips our security checks. To actually connect, click 'Connect eBay seller account' below.");
    else if (status === "error") toast.error("Something went wrong connecting eBay — please retry");
    searchParams.delete("ebay");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    api.get("/ebay/connection").then((res) => setEbayConn(res.data)).catch(() => setEbayConn({ connected: false }));
  }, []);

  useEffect(() => {
    if (user) {
      setBinderName(user.binder_name || "");
      setContactInfo(user.contact_info || "");
      setIsPublic(user.binder_public !== false);
      setShowValue(user.show_showcase_value !== false);
      setEmailPrefs({
        email_rank_alerts: user.email_rank_alerts !== false,
        email_chase_alerts: user.email_chase_alerts !== false,
        email_weekly_digest: user.email_weekly_digest !== false,
      });
    }
  }, [user]);

  useEffect(() => {
    api.get("/referral/me").then((res) => setReferral(res.data)).catch(() => {});
  }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      await api.patch("/me/settings", payload);
      await checkAuth();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save settings");
    } finally {
      setSaving(false);
    }
  };

  const togglePublic = async (value) => {
    setIsPublic(value);
    try {
      await api.patch("/me/settings", { binder_public: value });
      await checkAuth();
      toast.success(value ? "Your binder is now public" : "Your binder is now hidden");
    } catch {
      setIsPublic(!value);
      toast.error("Couldn't update visibility");
    }
  };

  const toggleShowValue = async (value) => {
    setShowValue(value);
    try {
      await api.patch("/me/settings", { show_showcase_value: value });
      await checkAuth();
      toast.success(value ? "Collection value is now shown on your showcase" : "Collection value hidden from your showcase");
    } catch {
      setShowValue(!value);
      toast.error("Couldn't update the setting");
    }
  };

  const toggleEmailPref = async (key, value) => {
    setEmailPrefs((prev) => ({ ...prev, [key]: value }));
    try {
      await api.patch("/me/settings", { [key]: value });
      toast.success(value ? "Email alerts on" : "Email alerts off");
    } catch {
      setEmailPrefs((prev) => ({ ...prev, [key]: !value }));
      toast.error("Couldn't update the setting");
    }
  };

  const referralLink = referral ? `${window.location.origin}/?ref=${referral.code}` : "";
  const copyReferral = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Invite link copied — share it anywhere");
  };

  const chooseTextSize = (size) => {
    setTextSizeState(size);
    setTextSize(size);
    toast.success(size === "default" ? "Text size reset" : "Text size increased");
  };

  const toggleReduceMotion = (value) => {
    setReduceMotionState(value);
    setReduceMotion(value);
    toast.success(value ? "Animations reduced" : "Animations back on");
  };

  const connectEbay = async () => {
    setEbayBusy(true);
    try {
      const res = await api.get("/ebay/oauth/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      setEbayBusy(false);
      toast.error(e.response?.data?.detail || "Couldn't start eBay connection");
    }
  };

  const disconnectEbay = async () => {
    if (!window.confirm("Disconnect your eBay seller account? You'll need to reconnect before creating any more eBay drafts.")) return;
    setEbayBusy(true);
    try {
      await api.delete("/ebay/connection");
      setEbayConn({ connected: false });
      toast.success("eBay account disconnected");
    } catch {
      toast.error("Couldn't disconnect");
    } finally {
      setEbayBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <h1 data-testid="settings-title" className="font-display text-4xl sm:text-5xl font-bold mb-2 flex items-center gap-3">
          <SettingsIcon className="w-8 h-8 text-[#A78BFA]" /> Settings
        </h1>
        <p className="text-zinc-400 text-sm mb-10">Your collector profile and privacy.</p>

        <div className="space-y-8">
          <section className="border border-white/10 bg-[#111113] rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-semibold mb-1">Binder name</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Your public collector name — shown on your cards in Explore search. Must be unique.
            </p>
            <div className="flex gap-2">
              <Input
                data-testid="settings-binder-name-input"
                value={binderName}
                onChange={(e) => setBinderName(e.target.value)}
                placeholder="e.g. Ash's Vault"
                maxLength={30}
                className="bg-black/30 border-white/10 flex-1"
              />
              <Button
                data-testid="settings-binder-name-save"
                onClick={() => save({ binder_name: binderName })}
                disabled={saving}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full shrink-0"
              >
                Save
              </Button>
            </div>
            {user && (
              <p className="text-xs text-zinc-500 mt-3">
                Your public binder:{" "}
                <Link data-testid="settings-public-binder-link" to={`/collector/${user.user_id}`} className="text-[#A78BFA] hover:underline">
                  cardark.../collector/{user.user_id.slice(-6)}
                </Link>
              </p>
            )}
          </section>

          <section className="border border-white/10 bg-[#111113] rounded-2xl p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
                  {isPublic ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-zinc-500" />}
                  Public binder
                </h2>
                <p className="text-xs text-zinc-500">
                  When on, your cards appear in Explore search and your binder page is viewable.
                  Turn off to hide everything from other collectors.
                </p>
              </div>
              <Switch data-testid="settings-public-toggle" checked={isPublic} onCheckedChange={togglePublic} />
            </div>
          </section>

          <section className="border border-white/10 bg-[#111113] rounded-2xl p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
                  {showValue ? <Eye className="w-4 h-4 text-[#F59E0B]" /> : <EyeOff className="w-4 h-4 text-zinc-500" />}
                  Collection value on showcase
                </h2>
                <p className="text-xs text-zinc-500">
                  When on, your shared showcase and public binder display your collection's total
                  estimated value. Turn off to keep the total private — individual card prices stay visible.
                </p>
              </div>
              <Switch data-testid="settings-show-value-toggle" checked={showValue} onCheckedChange={toggleShowValue} />
            </div>
          </section>

          <section className="border border-white/10 bg-[#111113] rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-semibold mb-1">Contact info for trades</h2>
            <p className="text-xs text-zinc-500 mb-4">
              Shared with the other collector only when a trade ping is accepted — e.g. your Discord
              handle. Never shown publicly.
            </p>
            <div className="flex gap-2">
              <Input
                data-testid="settings-contact-input"
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                placeholder="e.g. Discord: ashk#1234"
                maxLength={120}
                className="bg-black/30 border-white/10 flex-1"
              />
              <Button
                data-testid="settings-contact-save"
                onClick={() => save({ contact_info: contactInfo })}
                disabled={saving}
                className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full shrink-0"
              >
                Save
              </Button>
            </div>
          </section>

          <section data-testid="settings-accessibility" className="border border-white/10 bg-[#111113] rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
              <Accessibility className="w-4 h-4 text-[#38BDF8]" /> Accessibility
            </h2>
            <p className="text-xs text-zinc-500 mb-5">Saved on this device.</p>
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <div>
                <p className="text-sm font-medium">Text size</p>
                <p className="text-xs text-zinc-500">Make everything across the app bigger and easier to read.</p>
              </div>
              <div className="flex gap-1.5">
                {TEXT_SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    data-testid={`settings-text-size-${opt.value}`}
                    onClick={() => chooseTextSize(opt.value)}
                    className={`px-3 py-1.5 rounded-full border transition-colors ${opt.sample} ${textSize === opt.value ? "border-[#38BDF8] text-[#38BDF8] bg-[#38BDF8]/10 font-semibold" : "border-white/15 text-zinc-400 hover:text-white"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Reduce motion</p>
                <p className="text-xs text-zinc-500">Turn off animations and transitions across the app.</p>
              </div>
              <Switch data-testid="settings-reduce-motion-toggle" checked={reduceMotion} onCheckedChange={toggleReduceMotion} />
            </div>
          </section>

          <section data-testid="settings-email-prefs" className="border border-white/10 bg-[#111113] rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#A78BFA]" /> Email notifications
            </h2>
            <p className="text-xs text-zinc-500 mb-5">Sent to {user?.email}. In-app alerts always stay on.</p>
            <div className="space-y-5">
              {EMAIL_PREFS.map((pref) => (
                <div key={pref.key} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{pref.label}</p>
                    <p className="text-xs text-zinc-500">{pref.desc}</p>
                  </div>
                  <Switch
                    data-testid={`settings-${pref.key.replaceAll("_", "-")}-toggle`}
                    checked={emailPrefs[pref.key] !== false}
                    onCheckedChange={(v) => toggleEmailPref(pref.key, v)}
                  />
                </div>
              ))}
            </div>
          </section>

          <section data-testid="settings-ebay" className="border border-violet-400/20 bg-violet-500/[0.04] rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-[#A78BFA]" /> eBay seller account
            </h2>
            <p className="text-xs text-zinc-500 mb-4">
              Connect your eBay account to create <span className="text-violet-200 font-medium">draft listings</span> directly from your binder. Drafts land in your eBay Seller Hub so you can review and publish them yourself — CardArk never posts anything live without your click.
            </p>
            {ebayConn?.connected ? (
              <div className="space-y-3">
                <div data-testid="settings-ebay-status" className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-emerald-300 font-medium">Connected</span>
                  {ebayConn.ebay_user_id && (
                    <span className="text-zinc-500">· eBay user <span className="font-mono-plex text-zinc-300">{ebayConn.ebay_user_id}</span></span>
                  )}
                </div>
                <Button
                  data-testid="settings-ebay-disconnect"
                  onClick={disconnectEbay}
                  disabled={ebayBusy}
                  variant="outline"
                  className="border-white/20 hover:bg-white/5 rounded-full"
                >
                  <Unlink className="w-4 h-4 mr-1.5" /> Disconnect eBay
                </Button>
              </div>
            ) : (
              <Button
                data-testid="settings-ebay-connect"
                onClick={connectEbay}
                disabled={ebayBusy || ebayConn === null}
                className="bg-[#A78BFA] hover:bg-[#8B6FE8] text-black font-semibold rounded-full"
              >
                <Link2 className="w-4 h-4 mr-1.5" /> Connect eBay seller account
              </Button>
            )}
          </section>

          <section data-testid="settings-referral" className="border border-emerald-400/20 bg-emerald-500/[0.04] rounded-2xl p-6">
            <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
              <Gift className="w-4 h-4 text-emerald-400" /> Invite collectors, earn credits
            </h2>
            <p className="text-xs text-zinc-500 mb-4">
              Share your invite link — when a new collector signs up with it, you <span className="text-emerald-300 font-semibold">both get {referral?.credits_per_referral ?? 10} free scan credits</span>.
            </p>
            {referral ? (
              <>
                <div className="flex gap-2">
                  <Input
                    data-testid="settings-referral-link"
                    readOnly
                    value={referralLink}
                    onFocus={(e) => e.target.select()}
                    className="bg-black/30 border-white/10 flex-1 text-xs font-mono-plex"
                  />
                  <Button
                    data-testid="settings-referral-copy"
                    onClick={copyReferral}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full shrink-0"
                  >
                    <Copy className="w-4 h-4 mr-1.5" /> Copy
                  </Button>
                </div>
                <p data-testid="settings-referral-stats" className="text-xs text-zinc-500 mt-3">
                  {referral.referred_count > 0
                    ? <><span className="text-emerald-300 font-semibold">{referral.referred_count}</span> collector{referral.referred_count > 1 ? "s" : ""} joined with your link · <span className="text-emerald-300 font-semibold">+{referral.credits_earned}</span> credits earned</>
                    : "No sign-ups yet — drop your link in a Discord server or group chat."}
                </p>
              </>
            ) : (
              <p className="text-xs text-zinc-600 animate-pulse">Loading your invite link...</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
